import { useCallback, useEffect, useMemo, useRef, useState } from "react";

const RECORDING_CHUNK_INTERVAL_MS = 5000;
const DEFAULT_UPLOAD_CHUNK_BYTES = 256 * 1024;

function normalizeBaseUrl(value) {
  if (!value) {
    return "";
  }
  return String(value).trim().replace(/\/+$/, "");
}

function resolveUploadUrl() {
  const explicitBase =
    normalizeBaseUrl(process.env.REACT_APP_EXTERNALINTERVIEW_API_BASE_URL) ||
    normalizeBaseUrl(process.env.REACT_APP_BASE_URL);
  const fallbackBase = normalizeBaseUrl(window.location.origin);
  const base = explicitBase || fallbackBase;

  if (/\/api$/i.test(base)) {
    return `${base}/externalinterview/recording/upload/`;
  }
  return `${base}/api/externalinterview/recording/upload/`;
}

function resolveUploadChunkBytes() {
  const rawValue = Number(process.env.REACT_APP_EXTERNALINTERVIEW_UPLOAD_CHUNK_BYTES);
  if (!Number.isFinite(rawValue) || rawValue <= 0) {
    return DEFAULT_UPLOAD_CHUNK_BYTES;
  }
  return Math.floor(rawValue);
}

function stopDisplayStream(stream) {
  if (!stream) {
    return;
  }
  stream.getTracks().forEach((track) => track.stop());
}

export default function useCandidateRecorder({ enabled, roomId, email, userType }) {
  const recorderRef = useRef(null);
  const displayStreamRef = useRef(null);
  const chunksRef = useRef([]);
  const recordingStartedRef = useRef(false);
  const startInFlightRef = useRef(false);

  const [isRecording, setIsRecording] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);

  const roleLabel = useMemo(
    () => (userType || "unknown").toUpperCase(),
    [userType]
  );

  const uploadRecording = useCallback(async () => {
    if (chunksRef.current.length === 0) {
      return null;
    }

    setIsUploading(true);
    setUploadError(null);

    try {
      const blob = new Blob(chunksRef.current, { type: "video/webm" });
      const uploadUrl = resolveUploadUrl();
      const uploadChunkBytes = resolveUploadChunkBytes();
      const totalChunks = Math.max(1, Math.ceil(blob.size / uploadChunkBytes));
      const uploadId = `${roomId}-${Date.now()}`;

      let responsePayload = null;
      for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex += 1) {
        const chunkStart = chunkIndex * uploadChunkBytes;
        const chunkEnd = Math.min(blob.size, chunkStart + uploadChunkBytes);
        const chunkBlob = blob.slice(chunkStart, chunkEnd, "video/webm");

        const formData = new FormData();
        formData.append("room_id", roomId);
        formData.append("email", email || "");
        formData.append("user_type", "candidate");
        formData.append("upload_id", uploadId);
        formData.append("chunk_index", String(chunkIndex));
        formData.append("total_chunks", String(totalChunks));
        formData.append("file", chunkBlob, `candidate-recording-${chunkIndex}.webm`);

        const response = await fetch(uploadUrl, {
          method: "POST",
          body: formData,
          credentials: "include",
        });

        if (!response.ok) {
          throw new Error(`Upload failed with status ${response.status}`);
        }

        responsePayload = await response.json();
      }

      console.log(`[${roleLabel}][RECORDING_UPLOAD_SUCCESS]`, responsePayload);
      chunksRef.current = [];
      return responsePayload;
    } catch (error) {
      setUploadError(error);
      console.log(`[${roleLabel}][RECORDING_UPLOAD_ERROR]`, error);
      return null;
    } finally {
      setIsUploading(false);
    }
  }, [email, roleLabel, roomId]);

  const stopRecording = useCallback(async () => {
    if (!recordingStartedRef.current) {
      return;
    }

    recordingStartedRef.current = false;
    startInFlightRef.current = false;

    const recorder = recorderRef.current;
    if (recorder && recorder.state !== "inactive") {
      await new Promise((resolve) => {
        recorder.onstop = resolve;
        recorder.stop();
      });
    }

    stopDisplayStream(displayStreamRef.current);
    displayStreamRef.current = null;
    recorderRef.current = null;
    setIsRecording(false);

    await uploadRecording();
  }, [uploadRecording]);

  const startRecording = useCallback(async () => {
    if (!enabled || userType !== "candidate") {
      return;
    }

    if (recordingStartedRef.current || startInFlightRef.current) {
      return;
    }

    startInFlightRef.current = true;
    chunksRef.current = [];

    try {
      const displayStream = await navigator.mediaDevices.getDisplayMedia({
        video: true,
        audio: true,
      });

      const preferredMimeTypes = [
        "video/webm;codecs=vp9,opus",
        "video/webm;codecs=vp8,opus",
        "video/webm",
      ];

      const mimeType = preferredMimeTypes.find((type) => MediaRecorder.isTypeSupported(type));
      const mediaRecorder = mimeType
        ? new MediaRecorder(displayStream, { mimeType })
        : new MediaRecorder(displayStream);

      mediaRecorder.ondataavailable = (event) => {
        if (!event.data || event.data.size === 0) {
          return;
        }
        chunksRef.current.push(event.data);
        console.log(`[${roleLabel}][RECORDING_CHUNK_READY]`, {
          chunk_size: event.data.size,
          chunks_buffered: chunksRef.current.length,
        });
      };

      mediaRecorder.start(RECORDING_CHUNK_INTERVAL_MS);
      recorderRef.current = mediaRecorder;
      displayStreamRef.current = displayStream;
      recordingStartedRef.current = true;
      startInFlightRef.current = false;
      setIsRecording(true);

      displayStream.getVideoTracks().forEach((track) => {
        track.onended = () => {
          stopRecording();
        };
      });
    } catch (error) {
      startInFlightRef.current = false;
      recordingStartedRef.current = false;
      setIsRecording(false);
      console.log(`[${roleLabel}][RECORDING_START_ERROR]`, error);
    }
  }, [enabled, roleLabel, stopRecording, userType]);

  useEffect(() => {
    if (!enabled && recordingStartedRef.current) {
      stopRecording();
    }
  }, [enabled, stopRecording]);

  useEffect(() => {
    return () => {
      stopRecording();
    };
  }, [stopRecording]);

  return {
    isRecording,
    isUploading,
    uploadError,
    startRecording,
    stopRecording,
  };
}
